import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader } from 'src/components/Card';
import { Dialog } from 'src/components/Dialog';
import { Grid } from 'src/components/Grid';
import { SearchParamsPagination } from 'src/components/Pagination';
import { Scroller } from 'src/components/Scroller';
import { SectionToolbar } from 'src/components/SectionToolbar';
import { DataTable } from 'src/components/Table/DataTable';
import { Typography } from 'src/components/Typography';
import {
  FranchiseFeeLineItem,
  fetchFranchiseFeeLineItems,
  fetchFranchiseFeeLineItemsQueryKey,
} from 'src/models/hq/franchise-fee-line-item/franchise-fee-line-item';
import { fetchAllPlatforms } from 'src/models/platform/platform';
import { platformLabel } from 'src/models/platform/utils';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { ContainerBreakout } from 'src/shared/AppFrame/ContainerBreakout';
import { DateRangeFilter } from 'src/shared/Filters/DateRangeFilter';
import { Filters } from 'src/shared/Filters/Filters';
import { SelectFilter } from 'src/shared/Filters/SelectFilter';
import { SiteFilter } from 'src/shared/Filters/SiteFilter';
import { useHQPermissionCheck } from 'src/shared/HQ/useHQPermissionCheck';
import {
  formatFullDate,
  formatFullDateAndTime,
  formatMonthDayYear,
} from 'src/utils/dateFormatter';
import { formatCurrency } from 'src/utils/numberFormatter';
import { useListSearchParams } from 'src/utils/useListSearchParams';

import { useFranchiseFeePage } from './useFranchiseFeePage';

export default function ActivityPage() {
  const { shouldRestrictToMyLocations } = useHQPermissionCheck();
  const { t } = useTranslation('translation', {
    keyPrefix: 'franchiseFees.page.activity',
  });

  const [selectedLineItem, setSelectedLineItem] =
    useState<FranchiseFeeLineItem | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const {
    params,
    filterParams,
    updateParam,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  } = useListSearchParams({
    defaultPerPage: 20,
  });

  const { fees, feesIsLoading } = useFranchiseFeePage(undefined);

  const {
    data: lineItemsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: fetchFranchiseFeeLineItemsQueryKey({
      page: params.page,
      perPage: params.perPage,
      search: params.search,
      sortBy: params.sortBy,
      sortDir: params.sortDir,
      filters: {
        lineType: filterParams.lineType,
        createdAt: filterParams.createdAt,
        franchiseFeeId: filterParams.franchiseFeeId,
        sites: filterParams.sites,
        payoutId: filterParams.payoutId,
      },
    }),
    queryFn: () =>
      fetchFranchiseFeeLineItems({
        page: params.page,
        perPage: params.perPage,
        search: params.search,
        sortBy: params.sortBy,
        sortDir: params.sortDir,
        filters: {
          lineType: filterParams.lineType,
          createdAt: filterParams.createdAt,
          franchiseFeeId: filterParams.franchiseFeeId,
          sites: filterParams.sites,
          payoutId: filterParams.payoutId,
        },
      }),
  });

  const { data: platforms, isLoading: isLoadingPlatforms } = useQuery({
    queryKey: ['platforms'],
    queryFn: async () => {
      const response = await fetchAllPlatforms();

      return response || [];
    },
  });

  const feeName = (lineItem: FranchiseFeeLineItem) => {
    if (feesIsLoading) return '-';
    return fees.franchiseFees.find((fee) => fee.id === lineItem?.franchiseFeeId)
      ?.name;
  };

  const payerName = (lineItem: FranchiseFeeLineItem) => {
    if (isLoadingPlatforms) return '-';
    return lineItem?.payerType === 'Platform'
      ? platformLabel(
          platforms?.find((platform) => platform.id === lineItem?.payerId)
        ) || 'Unknown Platform'
      : lineItem?.payerType + ' ' + lineItem?.payerId;
  };

  const filterConfigs = useMemo(
    () => [
      {
        field: 'lineType',
        label: t('filters.lineType'),
        Component: SelectFilter,
        props: {
          options: [
            { value: 'charge', label: t('lineTypes.charge') },
            { value: 'refund', label: t('lineTypes.refund') },
            { value: 'recurring', label: t('lineTypes.recurring') },
          ],
        },
      },
      {
        field: 'createdAt',
        label: t('filters.createdAt'),
        Component: DateRangeFilter,
      },
      {
        field: 'franchiseFeeId',
        label: t('filters.franchiseFee'),
        Component: SelectFilter,
        props: {
          options: fees.franchiseFees.map((fee) => ({
            value: fee.id,
            label: fee.name,
          })),
        },
      },
      {
        field: 'sites',
        label: t('filters.payer'),
        Component: SiteFilter,
        showMyLocationsOnly: shouldRestrictToMyLocations(
          'franchiseFees.activity'
        ),
      },
    ],
    [t, fees.franchiseFees, shouldRestrictToMyLocations]
  );

  const columns = [
    {
      id: 'createdAt',
      label: t('columns.date'),
      sortable: true,
      format: (value: string) => formatFullDate(value),
    },
    {
      id: 'franchiseFeeId',
      label: t('columns.franchiseFee'),
      sortable: true,
      format: (value: string, row: FranchiseFeeLineItem) => feeName(row) || '-',
    },
    {
      id: 'lineType',
      label: t('columns.lineType'),
      sortable: true,
      format: (value: string) => t(`lineTypes.${value}`),
    },
    {
      id: 'amount',
      label: t('columns.amount'),
      sortable: true,
      format: (value: number) => formatCurrency(value),
    },
    {
      id: 'payer',
      label: t('columns.payer'),
      sortable: false,
      format: (value: string, row: FranchiseFeeLineItem) => payerName(row),
    },
  ];

  const handleSortBy = (columnId: string | ((prev: string) => string)) => {
    const value =
      typeof columnId === 'function' ? columnId(params.sortBy) : columnId;
    updateParam('sortBy', value);
  };

  const handleSortDir = (
    direction: 'asc' | 'desc' | ((prev: 'asc' | 'desc') => 'asc' | 'desc')
  ) => {
    const value =
      typeof direction === 'function' ? direction(params.sortDir) : direction;
    updateParam('sortDir', value);
  };

  const handleRowClick = (row: any) => {
    setSelectedLineItem(row as FranchiseFeeLineItem);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedLineItem(null);
  };

  const formatRefundAmount = (
    amount: number,
    lineType: 'charge' | 'refund' | 'recurring'
  ) => {
    return lineType === 'refund' ? 0 - amount : amount;
  };

  return (
    <>
      <AppFrameConfig
        id="franchise-fees"
        containerMaxWidth={'xl'}
        title={t('title')}
      />
      <ContainerBreakout>
        <SectionToolbar sx={{ pb: 3, pt: 0, px: 0 }}>
          <Scroller
            direction="row"
            spacing={2}
            hideScrollbar
            sx={{ mx: -2, px: 2, boxSizing: 'content-box' }}
          >
            <Filters
              filters={filterConfigs}
              params={params}
              updateParam={updateParam}
              updateFilterParam={updateFilterParam}
              getFilterParamValue={getFilterParamValue}
              resetFilterParams={resetFilterParams}
            />
          </Scroller>
        </SectionToolbar>
        <Card sx={{ mb: 6 }}>
          <DataTable
            columns={columns}
            rows={lineItemsData?.franchiseFeeLineItems || []}
            noResultsText={
              isError ? t('errorLoadingActivity') : t('noActivityFound')
            }
            loading={isLoading}
            totalItems={lineItemsData?.total || 0}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            setSortBy={handleSortBy}
            setSortDir={handleSortDir}
            onClickRow={handleRowClick}
          />
          <SearchParamsPagination
            params={params}
            updateParam={updateParam}
            total={lineItemsData?.total || 0}
            itemsPerPage={params.perPage}
          />
        </Card>
      </ContainerBreakout>

      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        title={t('dialog.title')}
        maxWidth="md"
        actions={[
          {
            label: t('dialog.close'),
            onClick: handleCloseDialog,
            color: 'primary',
            variant: 'contained',
          },
        ]}
      >
        {selectedLineItem && (
          <>
            <Grid container spacing={2} sx={{ p: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('dialog.fields.createdAt')}
                </Typography>
                <Typography variant="body1">
                  {formatFullDateAndTime(selectedLineItem.createdAt)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('dialog.fields.lineType')}
                </Typography>
                <Typography variant="body1">
                  {t(`lineTypes.${selectedLineItem.lineType}`)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('dialog.fields.amount')}
                </Typography>
                <Typography variant="body1">
                  {formatCurrency(selectedLineItem.amount)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('dialog.fields.payer')}
                </Typography>
                <Typography variant="body1">
                  {payerName(selectedLineItem)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('dialog.fields.franchiseFee')}
                </Typography>
                <Typography variant="body1">
                  {feeName(selectedLineItem)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('dialog.fields.franchiseFeePayoutId')}
                </Typography>
                <Typography variant="body1">
                  {selectedLineItem.franchiseFeePayoutId || '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('dialog.fields.feePeriodStart')}
                </Typography>
                <Typography variant="body1">
                  {formatMonthDayYear(selectedLineItem.feePeriodStart, 'UTC')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('dialog.fields.feePeriodEnd')}
                </Typography>
                <Typography variant="body1">
                  {formatMonthDayYear(selectedLineItem.feePeriodEnd, 'UTC')}
                </Typography>
              </Grid>
            </Grid>
            {/* purchase item info */}
            {selectedLineItem.purchaseItemId && (
              <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                <Card>
                  <CardHeader title={t('dialog.fields.purchaseItemInfo')} />
                  <Grid container spacing={2} sx={{ p: 2 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('dialog.fields.transactionAmount')}
                      </Typography>
                      <Typography variant="body1">
                        {formatCurrency(
                          formatRefundAmount(
                            selectedLineItem?.transactionAmount || 0,
                            selectedLineItem?.lineType || 'charge'
                          )
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('dialog.fields.chargeId')}
                      </Typography>
                      <Typography variant="body1">
                        {selectedLineItem.chargeId || '-'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('dialog.fields.refundId')}
                      </Typography>
                      <Typography variant="body1">
                        {selectedLineItem.refundId || '-'}
                      </Typography>
                    </Grid>
                    {selectedLineItem.meta?.itemName && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('dialog.fields.itemName')}
                        </Typography>
                        <Typography variant="body2">
                          {selectedLineItem.meta.itemName}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Card>
              </Grid>
            )}
            {/* end purchase item info */}
          </>
        )}
      </Dialog>
    </>
  );
}
